// Basic
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
// Libs
import { cn } from '@/common-utils/libraries/tailwind/utils';
import { useTranslation } from 'react-i18next';
// Store & actions
import useAssetsStore from '@/app/store/assets';
import useCustomPayStore from '@/app/store/customPay';
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSessionStore from '@/app/store/session';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Logo, { LogoCustomPay } from '@/common-utils/components/renders/logo/Logo';
import SidebarPreviousButton from './SidebarPreviousButton';
import { SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/common-utils/components/ui/sidebar';
// Common
import RouteUrls from '@/app/root/enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import { replaceParameters } from '@/common-utils/functions/restUtils';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import HiddenPermissions from '@/app/permissions/HiddenPermissions';
import Permissions from '@/app/permissions/Permissions';
import compareSimilarUrls from '@/common-utils/functions/compareSimilarUrls';

/**
 * @param root0
 * @param root0.dynamicTextColor
 * @param root0.isLight
 * @param root0.activeButton
 * @param root0.setActiveButton
 * @param root0.showBtnPreviousUrl
 * @param root0.setShowBtnPreviousUrl
 *  @returns {JSX} SidebarComplexHeader
 */
function SidebarComplexHeader({ dynamicTextColor, isLight, activeButton, setActiveButton, showBtnPreviousUrl, setShowBtnPreviousUrl }) {

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const isCustomPay = useCustomPayStore(s => s.isCustomPay);
    const assets = useAssetsStore(s => s.assets);
    const permissions = useSessionStore(s => s.session?.permissions);

    // Translation
    const { t: translate } = useTranslation();

    // URL
    const urlPathname = useLocation().pathname;

    // Hooks
    const { setOpen, setOpenMobile, isMobile, open: isOpenSidebar } = useSidebar();

    // Constants
    // Check if the user has the required permission to access the organization details
    const hasPermissionsToOrganizationDetails = hasRequiredPermissions({
        permissions,
        requiredPermissions: [Permissions.ORGANIZATION_READ.name]
    });

    // Functions
    const getLinkClassName = (buttonId) => {
        const baseClassName = 'sb-header-btn';
        const hoverClassName = isLight ? 'hover-light' : 'hover-dark';
        const activeClassName = activeButton === buttonId ? (isLight ? 'is-active-light' : 'is-active-dark') : '';
        return cn(baseClassName, hoverClassName, activeClassName);
    };

    // Hooks
    useEffect(() => {
        if (urlPathname === RouteUrls.HOME.url) {
            setActiveButton('home-btn');
        } else if (compareSimilarUrls(urlPathname, RouteUrls.ORGANIZATION_DETAILS.url)) {
            setActiveButton('sb-header-btn');
        }
    }, [urlPathname]);

    return (
        <SidebarHeader className='p-0'>
            <SidebarMenu className='bg-white p-2'>
                <SidebarMenuItem>
                    <Link to={
                        replaceParameters(
                            RouteUrls.HOME.url,
                            { organizationId: rootOrganization.uuid }
                        )}
                        onClick={() => {
                            setShowBtnPreviousUrl(false);
                            if (isMobile) {
                                setOpenMobile(false);
                            } else {
                                setOpen(false);
                            }
                            setActiveButton(isCustomPay ? '' : 'home-btn');
                        }}>
                        {isCustomPay &&
                            <LogoCustomPay assets={assets} className={'brand-logo rounded-lg min-h-8 flex m-auto'} logoClassName='max-h-28 m-auto' />
                        }
                        {!isCustomPay &&
                            <Logo assets={assets} className={'brand-logo rounded-lg min-h-8 flex m-auto'} logoClassName='max-h-28 m-auto' />
                        }
                    </Link>
                </SidebarMenuItem>
            </SidebarMenu>
            {
                !showBtnPreviousUrl &&
                <SidebarMenu className='p-2' data-cy='root-organization-details'>
                    <SidebarMenuItem className='space-y-4'>
                        {hasPermissionsToOrganizationDetails ? (
                            <SidebarMenuButton size='lg' tooltip={rootOrganization.legalName} className={'sb-header-btn'} asChild>
                                <Link to={
                                    replaceParameters(
                                        RouteUrls.ORGANIZATION_DETAILS.url,
                                        { organizationId: rootOrganization.uuid }
                                    )}
                                    onClick={() => {
                                        if (isMobile) {
                                            setOpenMobile(false);
                                        } else {
                                            setOpen(false);
                                        }
                                        setActiveButton('sb-header-btn');
                                    }}
                                    className={getLinkClassName('sb-header-btn')}
                                >
                                    <div className='flex aspect-square size-8 items-center justify-center rounded-lg'>
                                        <Icon icon={ICONS.ORGANIZATION} className={'!text-4xl'} />
                                    </div>
                                    <div className='grid text-left text-sm leading-tight'>
                                        <span className={'truncate font-semibold'} style={{ color: dynamicTextColor }}>{rootOrganization.legalName}</span>
                                        <Entity className={'truncate text-xs'} style={{ color: dynamicTextColor }} entity='sidebar.root_organization_helper' />
                                    </div>
                                </Link>
                            </SidebarMenuButton>
                        ) : (
                            <SidebarMenuButton size='lg' tooltip={rootOrganization.legalName} className={getLinkClassName('sb-header-btn')}>
                                <div className='flex aspect-square size-8 items-center justify-center rounded-lg'>
                                    <Icon icon={ICONS.ORGANIZATION} className={'!text-4xl'} />
                                </div>
                                <div className='grid text-left text-sm leading-tight'>
                                    <span className={'truncate font-semibold'} style={{ color: dynamicTextColor }}>{rootOrganization.legalName}</span>
                                    <Entity className={'truncate text-xs'} style={{ color: dynamicTextColor }} entity='sidebar.root_organization_helper' />
                                </div>
                            </SidebarMenuButton>
                        )}
                        {!isCustomPay && hasRequiredPermissions({ permissions: permissions, requiredPermissions: [HiddenPermissions.DASHBOARD_ACCESS.name] }) &&
                            (<SidebarMenuButton
                                data-cy='dashboard-page'
                                asChild
                                tooltip={translate('page.dashboard.title')}
                                className={cn(
                                    isOpenSidebar
                                        ? 'justify-start px-2 gap-2'
                                        : 'justify-center px-0 gap-0'
                                )}
                            >
                                <Link
                                    to={RouteUrls.HOME.url}
                                    onClick={() => {
                                        if (isMobile) {
                                            setOpenMobile(false);
                                        } else {
                                            setOpen(false);
                                        }
                                        setActiveButton('home-btn');
                                    }}
                                    className={getLinkClassName('home-btn')}
                                >
                                    <Icon icon={ICONS.DASHBOARD} />
                                    {isOpenSidebar && (
                                        <Entity entity='page.dashboard.title' style={{ color: dynamicTextColor }} />
                                    )}
                                </Link>
                            </SidebarMenuButton>
                            )
                        }
                    </SidebarMenuItem>
                </SidebarMenu>
            }
            {
                showBtnPreviousUrl &&
                <SidebarPreviousButton dynamicTextColor={dynamicTextColor} linkClassName={getLinkClassName()} />
            }
        </SidebarHeader>
    );
}

SidebarComplexHeader.propTypes = {
    dynamicTextColor: PropTypes.string.isRequired,
    isLight: PropTypes.bool.isRequired,
    activeButton: PropTypes.string.isRequired,
    setActiveButton: PropTypes.func.isRequired,
    showBtnPreviousUrl: PropTypes.bool.isRequired,
    setShowBtnPreviousUrl: PropTypes.func.isRequired
};

export default SidebarComplexHeader;